import {
    type ReactElement,
    type ReactNode,
    cloneElement,
    useState,
    useMemo,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IButtonGroupMenuProps from "./components/menu/type";
import type IButtonGroupProps from "./type";
import stylesheet from "./stylesheet";
import ButtonGroupMenu from "./components/menu";
import ButtonGroupSeparator from "./components/separator";
import ButtonGroupText from "./components/text";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    AttachedGroupItemContextType
} from "../attachedGroup/type";
import type IButtonProps from "../button/type";
import {
    flattenAttachedGroupChildren,
    getAttachedGroupOuterRadius,
    getAttachedGroupItemStyle,
    getAttachedGroupPosition
} from "../attachedGroup/stylesheet";
import {
    AttachedGroupItemContext
} from "../attachedGroup/context";
import {
    getControlMeasures
} from "../../helpers/theme/control";
import {
    getHugStyle
} from "../../helpers/theme/spread";
import Button from "../button";

const ButtonGroupItemProvider: FC<AttachedGroupItemContextType & {
    children: ReactNode;
}> = ({
    isShadowOwner,
    toggleVariant,
    groupVariant,
    innerRadius,
    orientation,
    outerRadius,
    isAttached,
    isDisabled,
    isOverlap,
    children,
    position,
    size,
    type
}) => {
    const value = useMemo(() => ({
        toggleVariant,
        isShadowOwner,
        groupVariant,
        orientation,
        outerRadius,
        innerRadius,
        isDisabled,
        isAttached,
        isOverlap,
        position,
        size,
        type
    }), [
        toggleVariant,
        groupVariant,
        isShadowOwner,
        orientation,
        isDisabled,
        isAttached,
        outerRadius,
        innerRadius,
        isOverlap,
        position,
        size,
        type
    ]);

    return <AttachedGroupItemContext.Provider
        value={value}
    >
        {children}
    </AttachedGroupItemContext.Provider>;
};

const ButtonGroup: FC<IButtonGroupProps> = ({
    spreadBehaviour = "baseline",
    orientation = "horizontal",
    variant = "outline",
    isDisabled = false,
    type = "neutral",
    size: sizeProp,
    customTheme,
    children,
    style,
    title,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        radiuses,
        borders
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const [
        focusedIndex,
        setFocusedIndex
    ] = useState<number | undefined>(undefined);

    const size = sizeProp ?? (pointerType === "coarse" ? "large" : "medium");

    const currentMeasures = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const outerRadius = getAttachedGroupOuterRadius({
        minHeight: currentMeasures.minHeight,
        orientation,
        radiuses
    });

    const elements = flattenAttachedGroupChildren({
        children
    });

    const itemCount = elements.filter((element) => element.type !== ButtonGroupSeparator).length;

    const webProps = Platform.OS === "web" ? {
        "aria-orientation": orientation
    } : {};

    const renderElement = (element: ReactElement, index: number, itemIndex: number) => {
        const position = getAttachedGroupPosition({
            count: itemCount,
            index: itemIndex
        });

        const contextProps = {
            isOverlap: variant === "outline",
            innerRadius: radiuses.none,
            groupVariant: variant,
            isShadowOwner: false,
            isAttached: true,
            outerRadius,
            orientation,
            isDisabled,
            position,
            size,
            type
        };

        if(element.type !== Button) {
            const currentElement = element.type === ButtonGroupMenu ? cloneElement(element as ReactElement<IButtonGroupMenuProps>, {
                customTheme: (element as ReactElement<IButtonGroupMenuProps>).props.customTheme ?? customTheme
            }) : element;

            return <ButtonGroupItemProvider
                key={element.key ?? index}
                {...contextProps}
            >
                {currentElement}
            </ButtonGroupItemProvider>;
        }

        const buttonElement = element as ReactElement<IButtonProps>;

        const attachedStyle = getAttachedGroupItemStyle({
            isDisabled: isDisabled || !!buttonElement.props.isDisabled,
            isFocused: focusedIndex === index,
            isOverlap: variant === "outline",
            innerRadius: radiuses.none,
            isActive: false,
            shapeConfigs,
            outerRadius,
            orientation,
            position,
            borders
        });

        const isIconOnly = !!buttonElement.props.icon && !buttonElement.props.title;

        const onFocus: NonNullable<IButtonProps["onFocus"]> = (event) => {
            setFocusedIndex(index);

            buttonElement.props.onFocus?.(event);
        };

        const onBlur: NonNullable<IButtonProps["onBlur"]> = (event) => {
            setFocusedIndex(undefined);

            buttonElement.props.onBlur?.(event);
        };

        return <ButtonGroupItemProvider
            key={element.key ?? index}
            {...contextProps}
        >
            {
                cloneElement(buttonElement, {
                    spreadBehaviour: orientation === "vertical" || (spreadBehaviour === "stretch" && !isIconOnly) ? "stretch" : "free",
                    customTheme: buttonElement.props.customTheme ?? customTheme,
                    isDisabled: isDisabled || buttonElement.props.isDisabled,
                    style: [
                        attachedStyle.container,
                        attachedStyle.surface,
                        buttonElement.props.style
                    ],
                    isPressScaleEnabled: false,
                    borderRadius: outerRadius,
                    variant,
                    onFocus,
                    onBlur,
                    size,
                    type
                })
            }
        </ButtonGroupItemProvider>;
    };

    const getItemIndex = (index: number) => {
        return elements.slice(0, index + 1).filter((element) => element.type !== ButtonGroupSeparator).length - 1;
    };

    return <View
        {...props}
        {...webProps}
        style={[
            style,
            stylesheet.container,
            orientation === "vertical" ? stylesheet.vertical : stylesheet.horizontal,
            spreadBehaviour === "stretch" ? stylesheet.stretch : getHugStyle({
                style
            })
        ]}
        accessibilityLabel={title}
        aria-label={title}
        role="group"
    >
        {
            elements.map((element, index) => renderElement(element, index, getItemIndex(index)))
        }
    </View>;
};
export default Object.assign(ButtonGroup, {
    Separator: ButtonGroupSeparator,
    Menu: ButtonGroupMenu,
    Text: ButtonGroupText
});
